/**
 * Affirmation Editor Component
 * Step 2.5 - User can view, edit, add, remove, and SELECT/DESELECT affirmations
 * Only selected affirmations will be used in the audio session
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Trash2, Check, Square, CheckSquare } from "lucide-react";
import { containerVariants, itemVariants, getModeStyles } from "./constants";
import VoiceBadge from "./Premium/VoiceBadge";
import useRecordedLines from "./Premium/useRecordedLines";

const AffirmationEditor = ({ 
  mode,
  affirmations = [],
  setAffirmations,
  onBack,
  onContinue
}) => {
  const [newAffirmation, setNewAffirmation] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const { isRecorded } = useRecordedLines();
  const [editText, setEditText] = useState("");
  
  // Track which affirmations are selected (all selected by default)
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  
  // Initialize all affirmations as selected when they change
  useEffect(() => {
    const allIndices = new Set(affirmations.map((_, i) => i));
    setSelectedIndices(allIndices);
  }, [affirmations.length]);

  const modeStyles = getModeStyles(mode);

  const handleToggleSelection = (index) => {
    const newSelected = new Set(selectedIndices);
    if (newSelected.has(index)) {
      // Don't allow deselecting if only 1 is selected (minimum 1)
      if (newSelected.size > 1) {
        newSelected.delete(index);
      }
    } else {
      newSelected.add(index);
    }
    setSelectedIndices(newSelected);
  };

  const handleSelectAll = () => {
    const allIndices = new Set(affirmations.map((_, i) => i));
    setSelectedIndices(allIndices);
  };

  const handleDeselectAll = () => {
    // Keep at least the first one selected
    setSelectedIndices(new Set([0]));
  };

  const handleAddAffirmation = () => {
    if (newAffirmation.trim()) {
      const newIndex = affirmations.length;
      setAffirmations([...affirmations, newAffirmation.trim()]);
      // Auto-select new affirmation
      setSelectedIndices(prev => new Set([...prev, newIndex]));
      setNewAffirmation("");
    }
  };

  const handleRemoveAffirmation = (index) => {
    // Remove from affirmations
    setAffirmations(affirmations.filter((_, i) => i !== index));
    
    // Update selected indices (shift indices after removed one)
    const newSelected = new Set();
    selectedIndices.forEach(i => {
      if (i < index) newSelected.add(i);
      else if (i > index) newSelected.add(i - 1);
      // if i === index, it's removed, so don't add
    });
    
    // Ensure at least one is selected
    if (newSelected.size === 0 && affirmations.length > 1) {
      newSelected.add(0);
    }
    setSelectedIndices(newSelected);
  };

  const handleEditAffirmation = (index) => {
    setEditingIndex(index);
    setEditText(affirmations[index]);
  };

  const handleSaveEdit = () => {
    if (editText.trim() && editingIndex !== null) {
      const updated = [...affirmations];
      updated[editingIndex] = editText.trim();
      setAffirmations(updated);
      setEditingIndex(null);
      setEditText("");
    }
  };

  // When continuing, only pass selected affirmations
  const handleContinue = () => {
    const selectedAffirmations = affirmations.filter((_, i) => selectedIndices.has(i));
    // Update parent with only selected affirmations
    setAffirmations(selectedAffirmations);
    onContinue();
  };

  const getStepLabel = () => {
    if (mode === "performance") return "Step 3 of 5";
    if (mode === "deep_recovery") return "Step 2 of 4";
    if (mode === "health_recovery") return "Step 2 of 4";
    return "Step 2 of 4";
  };

  const getTitle = () => {
    if (mode === "performance") return "Program Your Mind";
    if (mode === "deep_recovery") return "Your Healing Words";
    if (mode === "health_recovery") return "Your Healing Words";
    return "Your Reclaiming Words";
  };

  const selectedCount = selectedIndices.size;
  const totalCount = affirmations.length;

  return (
    <motion.div
      key="affirmation-editor"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0 }}
      className="h-full flex flex-col p-6"
    >
      <button onClick={onBack} className="text-white/60 hover:text-white mb-6" data-testid="affirmation-editor-back">
        <ArrowLeft size={24} />
      </button>

      <motion.p 
        variants={itemVariants}
        className={`text-sm uppercase tracking-wider ${modeStyles.textColor}/80 mb-2`}
      >
        {getStepLabel()}
      </motion.p>
      <motion.h2 
        variants={itemVariants}
        className="font-heading text-2xl text-white mb-2"
      >
        {getTitle()}
      </motion.h2>
      <motion.p 
        variants={itemVariants}
        className="text-white/50 text-sm mb-2"
      >
        These affirmations will be spoken during your session. Tap the checkbox to include or exclude.
      </motion.p>
      
      {/* Selection Counter & Quick Actions */}
      <motion.div 
        variants={itemVariants}
        className="flex items-center justify-between mb-4"
      >
        <span className={`text-sm ${modeStyles.textColor}`}>
          {selectedCount} of {totalCount} selected
        </span>
        <div className="flex gap-3">
          <button
            onClick={handleSelectAll}
            className="text-xs text-white/50 hover:text-white/80 transition-colors"
            data-testid="select-all-affirmations"
          >
            Select All
          </button>
          <span className="text-white/20">|</span>
          <button
            onClick={handleDeselectAll}
            className="text-xs text-white/50 hover:text-white/80 transition-colors"
            data-testid="deselect-all-affirmations"
          >
            Clear
          </button>
        </div>
      </motion.div>

      {/* Affirmation List with Checkboxes */}
      <motion.div 
        variants={itemVariants}
        className="flex-1 overflow-y-auto space-y-2 mb-4"
      >
        {affirmations.map((affirmation, index) => {
          const isSelected = selectedIndices.has(index);
          const isEditing = editingIndex === index;
          
          return (
            <div
              key={index}
              className={`p-4 rounded-xl border transition-all ${
                isEditing
                  ? `${modeStyles.bgGradient} border-white/30` 
                  : isSelected 
                    ? "bg-white/5 border-transparent hover:bg-white/10"
                    : "bg-white/[0.02] border-transparent opacity-50"
              }`}
              data-testid={`affirmation-item-${index}`}
            >
              {isEditing ? (
                <div className="space-y-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full bg-transparent text-white text-sm resize-none focus:outline-none"
                    rows={2}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingIndex(null)}
                      className="px-3 py-1 text-xs text-white/50 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className={`px-3 py-1 text-xs bg-gradient-to-r ${modeStyles.gradient} text-white rounded-full`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  {/* Checkbox for selection */}
                  <button
                    onClick={() => handleToggleSelection(index)}
                    className={`flex-shrink-0 mt-0.5 transition-colors ${
                      isSelected ? modeStyles.textColor : 'text-white/30'
                    }`}
                    data-testid={`affirmation-checkbox-${index}`}
                  >
                    {isSelected ? (
                      <CheckSquare size={22} />
                    ) : (
                      <Square size={22} />
                    )}
                  </button>
                  
                  {/* Affirmation text - click to edit */}
                  <p 
                    className={`flex-1 text-sm cursor-pointer transition-colors ${
                      isSelected ? 'text-white/80 hover:text-white' : 'text-white/40'
                    }`}
                    onClick={() => handleEditAffirmation(index)}
                  >
                    {affirmation}
                    {/* "🎙️ Your voice" badge — shows on any line that
                        matches Michael's recorded 2-Layer library so
                        users know which affirmations play in his
                        actual voice vs cloned TTS. */}
                    {isRecorded(affirmation) && (
                      <span className="ml-2 inline-flex align-middle">
                        <VoiceBadge present testid={`voice-badge-${index}`} />
                      </span>
                    )}
                  </p>
                  
                  {/* Delete button */}
                  <button
                    onClick={() => handleRemoveAffirmation(index)}
                    className="text-white/30 hover:text-red-400 transition-colors"
                    data-testid={`affirmation-delete-${index}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </motion.div>

      {/* Add New Affirmation */}
      <motion.div variants={itemVariants} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newAffirmation}
            onChange={(e) => setNewAffirmation(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddAffirmation()}
            placeholder="Add a personal affirmation..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-white/30"
            data-testid="add-affirmation-input"
          />
          <button
            onClick={handleAddAffirmation}
            disabled={!newAffirmation.trim()}
            className={`px-4 rounded-xl bg-gradient-to-r ${modeStyles.gradient} text-white disabled:opacity-50`}
            data-testid="add-affirmation-button"
          >
            <Plus size={20} />
          </button>
        </div>
      </motion.div>

      {/* Continue Button */}
      <motion.button
        variants={itemVariants}
        onClick={handleContinue}
        disabled={selectedCount === 0}
        className={`w-full py-4 rounded-full font-medium text-white bg-gradient-to-r ${modeStyles.gradient} hover:opacity-90 transition-all disabled:opacity-50`}
        data-testid="continue-after-affirmations"
      >
        Continue with {selectedCount} affirmation{selectedCount !== 1 ? 's' : ''}
      </motion.button>
    </motion.div>
  );
};

export default AffirmationEditor;
